import React, { createContext, useState, useRef, useContext, useEffect } from 'react';

const AudioContext = createContext();

export const useAudio = () => {
  return useContext(AudioContext);
};

export const AudioProvider = ({ children }) => {
  const [currentAudio, setCurrentAudio] = useState(null);
  const isPlayingRef = useRef(false);
  const audioRef = useRef(null);

  const playSound = async (audioUrl) => {
    // Don't try to play if no URL provided
    if (!audioUrl) {
      console.warn('No audio URL provided');
      return;
    }

    if (audioRef.current && audioRef.current.src === audioUrl) {

      if (isPlayingRef.current) {
        audioRef.current.pause();
        isPlayingRef.current = false;
      } else {
        audioRef.current.currentTime = 0; // Reset the current time to the start
        try {
          await audioRef.current.play();
          isPlayingRef.current = true;
        } catch (error) {
          console.warn('Audio playback failed:', error.message);
          isPlayingRef.current = false;
        }
      }
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener('ended', handleAudioEnded);
      audioRef.current.removeEventListener('error', handleAudioError);
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.addEventListener('ended', handleAudioEnded);
    audio.addEventListener('error', handleAudioError);

    try {
      await audio.play();
      setCurrentAudio(audioUrl);
      isPlayingRef.current = true;
    } catch (error) {
      console.warn('Audio playback failed:', error.message);
      isPlayingRef.current = false;
      setCurrentAudio(null);
    }
  };

  const handleAudioError = (e) => {
    console.warn('Audio error:', e.target.error?.message || 'Failed to load audio');
    isPlayingRef.current = false;
    setCurrentAudio(null);
  };

  const handleAudioEnded = () => {
    isPlayingRef.current = false;
  };

  const stopSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener('ended', handleAudioEnded);
      audioRef.current.removeEventListener('error', handleAudioError);
      audioRef.current = null;
    }
    setCurrentAudio(null);
    isPlayingRef.current = false;
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('ended', handleAudioEnded);
        audioRef.current.removeEventListener('error', handleAudioError);
      }
    };
  }, []);

  const value = {
    currentAudio,
    isPlayingRef,
    playSound,
    stopSound,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};
